import React from 'react';
import { useIntl } from 'react-intl';
import { MdCheckCircle } from 'react-icons/md';
import {
  SetupStatusWrapper,
  SetupStepRow,
  SetupStepPill,
  SetupStepHint,
  ToolChipSpinner,
} from './Chat.styled';
import { useSkillLoadLabel } from './skillLoadLabel';
import { formatElapsed, useElapsedSeconds } from './elapsed';
import type { SetupStep } from '@hooks/useChat';

const useStepLabel = (step: SetupStep, elapsed: number): string => {
  const intl = useIntl();
  const done = step.status === 'done';
  const skillLabel = useSkillLoadLabel(step.skill, done);

  if (step.kind === 'environment') {
    return done
      ? intl.formatMessage({ id: 'chat.environment.ready' })
      : intl.formatMessage({ id: 'chat.environment.allocating' }, { elapsed: formatElapsed(elapsed) });
  }
  return skillLabel;
};

const SetupStepItem: React.FC<{ step: SetupStep }> = ({ step }) => {
  const intl = useIntl();
  const done = step.status === 'done';
  const allocating = step.kind === 'environment' && !done;
  const elapsed = useElapsedSeconds(step.startedAt, allocating);
  const label = useStepLabel(step, elapsed);

  return (
    <SetupStepRow>
      <SetupStepPill $complete={done}>
        {done ? <MdCheckCircle size={16} /> : <ToolChipSpinner />}
        {label}
      </SetupStepPill>
      {allocating && (
        <SetupStepHint>
          {intl.formatMessage({ id: 'chat.environment.allocating.hint' })}
        </SetupStepHint>
      )}
    </SetupStepRow>
  );
};

/**
 * Transient status for the work the agent does before it can start: loading
 * skills, booting the environment. Steps render in the order they happened and
 * the whole block fades out when the turn ends.
 */
const SetupStatus: React.FC<{ steps: SetupStep[]; exiting: boolean }> = ({ steps, exiting }) => {
  if (steps.length === 0) return null;

  return (
    <SetupStatusWrapper $exiting={exiting} aria-live="polite" data-testid="setup-status">
      {steps.map((step) => (
        <SetupStepItem key={step.id} step={step} />
      ))}
    </SetupStatusWrapper>
  );
};

export default SetupStatus;
